import React, { useEffect } from 'react'
import styled from '@emotion/styled'
import {
  ConnectionCardSmallLoading,
  Divider,
  Grid,
  PageEmptyState,
  Theme,
  Grow,
} from '@yuno/design-system-web'
import type { Organization } from '@yuno/types'
import { ConnectionDrawerForm } from '../../components/connection-drawer-form'
import { ConnectionCardSmall } from '../../components/connection-card-small'
import useYourconnectionsHook from '../../hooks/your-connection-hook'
import {
  IInstructions,
  ITestKey,
  SetupInstructions,
} from '../../interfaces/connection.interfaces'
import { useTranslation } from 'react-i18next'

export interface ISelectedProvider {
  provider_name: string
  provider_id: string
  name: string
  icon: string
  description: string
  fields: any
  countries: string
  paymentMethod: string
  paymentMethods: Organization.PaymentMethods[]
  category: string
  code: string
  setup_instructions: Array<SetupInstructions>
  instructions: IInstructions[]
  test_parameters: ITestKey[]
}
interface YourConnectionsPageProps {
  onChange?: (e: React.SyntheticEvent, newValue: number) => void
}

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flexwrap: wrap;
`

const EmptyContainer = styled.div<{ theme?: Theme }>`
  width: 100%;
  height: 55vh;

  .explore-link {
    text-decoration: none;
    cursor: pointer;
    color: ${({ theme }) => theme.palette.vivid.brand};
  }
`

const EmptyContainerWithData = styled.div<{ theme?: Theme }>`
  height: 40px;
`

const DividerStyle = styled(Divider)<{ theme?: Theme }>`
  width: 100%;
  margin: 40px 0;
`

export const TitleArchived = styled('p')(({ theme }: { theme?: any }) => ({
  fontFamily: theme.typography['3xl'].regular.fontFamily,
  fontWeight: theme.typography['3xl'].regular.fontWeight,
  fontSize: theme.typography['3xl'].regular.fontSize,
  color: theme.palette.neutral.black,
  margin: '0 0 40px 0',
}))

const YourConnectionsPage = ({ onChange }: YourConnectionsPageProps) => {
  const {
    patchConnection,
    connectionResponse,
    handleSubmit,
    drawerData,
    selectedProvider,
    providerResponse,
    resetForm,
    openDrawerForm,
    handleCloseDrawer,
    isLoading,
    data,
    handlerFetchProviderParams,
    handlerArchive,
    showDeleteDialog,
  } = useYourconnectionsHook()
  const { t } = useTranslation()

  return (
    <Wrapper>
      {selectedProvider?.provider_id && (
        <ConnectionDrawerForm
          error={patchConnection?.error?.response.data.message}
          connectionResponse={connectionResponse?.data}
          status={patchConnection.status}
          handleSubmit={handleSubmit}
          category={drawerData?.category}
          name={selectedProvider?.provider_name}
          icon={selectedProvider?.icon}
          connectionName={drawerData?.name}
          description={drawerData?.description}
          fields={drawerData?.fields}
          paymentMethods={providerResponse?.data?.paymentMethods}
          isAsync={providerResponse?.data?.provider?.async}
          open={openDrawerForm}
          providerId={drawerData?.provider_id}
          countries={drawerData?.countries}
          onSecondaryAction={handleCloseDrawer}
          onPrimaryAction={resetForm}
          onClose={handleCloseDrawer}
          loading={patchConnection.isLoading}
          instructions={drawerData?.instructions}
          setupInstructions={drawerData?.setup_instructions}
          testKeyValues={drawerData?.test_parameters}
          currentMode={'edit'}
          integrationTypes={providerResponse?.data?.provider?.integration_types}
          providerResponse={providerResponse?.data}
        />
      )}
      {isLoading ? (
        <>
          <ConnectionCardSmallLoading />
          <ConnectionCardSmallLoading />
          <ConnectionCardSmallLoading />
        </>
      ) : (
        <Grid container>
          <Grid container>
            {data?.filter((t) => !t.archive)?.length > 0 ? (
              <Grid
                container
                spacing={2}
                wrap="wrap"
                flexDirection="row"
                justifyContent="flex-start"
              >
                {data
                  .filter((t) => !t.archive)
                  .map((item, key) => {
                    return (
                      <Grow in={true} timeout={(key + 1) * 250}>
                        <Grid xs={12} md={6} lg={4} xl={3}>
                          <ConnectionCardSmall
                            key={key}
                            integrationCode={item.code}
                            title={item?.provider_name}
                            name={item?.name}
                            category={item?.category || t('withoutCategory')}
                            img={item?.icon}
                            providerId={item?.provider_id}
                            handlerEdit={() =>
                              handlerFetchProviderParams(item.code)
                            }
                            handlerArchive={() =>
                              handlerArchive(
                                item.code,
                                true,
                                item?.provider_name,
                                true,
                              )
                            }
                            tooltipTitle={t('validatingDataHours')}
                            icon="Clock"
                            iconColor="info"
                            pending={item.status === 'PENDING'}
                          />
                        </Grid>
                      </Grow>
                    )
                  })}
              </Grid>
            ) : data?.length > 0 ? (
              <EmptyContainerWithData />
            ) : (
              <EmptyContainer>
                <PageEmptyState
                  title={t('noConnectionsAddedYet')}
                  subtitle={
                    <div>
                      {t('anyConnectionsYet')}
                      <a
                        className="explore-link"
                        onClick={(e) => onChange(e, 0)}
                      >
                        {t('anyConnectionsYetFinal')}
                      </a>
                      .
                    </div>
                  }
                />
              </EmptyContainer>
            )}
          </Grid>
          {data?.filter((t) => t.archive).length > 0 && (
            <>
              <DividerStyle />
              <Grid item xs={12}>
                <TitleArchived>Archived</TitleArchived>
              </Grid>
              <Grid container spacing={2}>
                {data?.length > 0 &&
                  data
                    .filter((t) => t.archive)
                    .map((item, key) => {
                      return (
                        <Grow in={true} timeout={(key + 1) * 250}>
                          <Grid
                            item
                            xs={12}
                            md={6}
                            lg={4}
                            xl={3}
                            key={item.provider_id}
                          >
                            <ConnectionCardSmall
                              fullWidth
                              title={item?.provider_name}
                              name={item?.name}
                              img={item?.icon}
                              category={'Archived'}
                              providerId={item?.provider_id}
                              handlerEdit={showDeleteDialog.bind(null, item)}
                              handlerArchive={handlerArchive.bind(
                                null,
                                item.code,
                                false,
                                item?.provider_name,
                                false,
                              )}
                              archived
                            />
                          </Grid>
                        </Grow>
                      )
                    })}
              </Grid>
            </>
          )}
        </Grid>
      )}
    </Wrapper>
  )
}
export default YourConnectionsPage
